/**
 * Cookie consent management (GDPR + CR Ley 8968).
 * No third-party trackers loaded today — categories are forward-looking.
 *
 * Functional enforcement:
 *   - `hasConsent(category)` is the single source of truth before loading
 *     analytics / marketing scripts.
 *   - Any change emits the global event "ibylit:consent-updated" AND the
 *     short-named alias "consent-updated" so listeners can react in real time.
 */
export type ConsentCategory = "essential" | "analytics" | "marketing";

export interface ConsentState {
  essential: true; // always required
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
}

const STORAGE_KEY = "ibylit-cookie-consent";
export const CONSENT_VERSION = "1.0";
export const CONSENT_EVENT = "ibylit:consent-updated";
export const CONSENT_EVENT_ALIAS = "consent-updated";
export const CONSENT_OPEN_EVENT = "ibylit:consent-open";

// In-memory mirror so reads are synchronous and resilient to storage failures.
let memoryState: ConsentState | null = null;

const readFromStorage = (): ConsentState | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

// Hydrate memory mirror at module load (browser only).
if (typeof window !== "undefined") {
  memoryState = readFromStorage();
}

export const getConsent = (): ConsentState | null => {
  if (memoryState) return memoryState;
  memoryState = readFromStorage();
  return memoryState;
};

/**
 * Functional gate. Use before initializing any non-essential script.
 * Essential is always true. If no decision exists yet, non-essential is denied.
 */
export const hasConsent = (category: ConsentCategory): boolean => {
  if (category === "essential") return true;
  const state = getConsent();
  if (!state) return false;
  return Boolean(state[category]);
};

const dispatchUpdate = (state: ConsentState) => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }));
    // Short-named alias for convenience listeners.
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT_ALIAS, { detail: state }));
  } catch {
    /* noop */
  }
};

export const saveConsent = (analytics: boolean, marketing: boolean): ConsentState => {
  const state: ConsentState = {
    essential: true,
    analytics,
    marketing,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  memoryState = state;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage blocked — memory mirror still authoritative for this session */
  }
  dispatchUpdate(state);
  return state;
};

export const acceptAll = () => saveConsent(true, true);
export const rejectAll = () => saveConsent(false, false);

export const clearConsent = () => {
  memoryState = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
};

/** Programmatically request the consent modal to reopen (e.g. footer button). */
export const openConsentPreferences = () => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
  } catch {
    /* noop */
  }
};
